import React, { useContext, useEffect, useState } from "react";
import { ThemeContext } from "../App";
import "./app-deep-link.scss";

// Single source of truth for app store links (used by Home, ContactUs, About, AppDeepLink)
export const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=gobicraft.tomko";
export const APP_STORE_URL = "https://apps.apple.com/gb/app/tomko/id6749509851";

const REDIRECTING_DURATION_MS = 2000;
const FALLBACK_DELAY_MS = 800;

// In-app browsers (Instagram, Facebook, etc.) often close the tab on programmatic
// custom-scheme redirects instead of opening the app. Only allow auto-redirect in normal browsers.
const isInAppBrowser = () => {
  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  return (
    /Instagram/i.test(ua) ||
    /FBAN|FBAV|FB_IAB/i.test(ua) ||
    /FBIOS|FBAN/i.test(ua) ||
    /Messenger/i.test(ua) ||
    /Line\//i.test(ua) ||
    /Twitter/i.test(ua) ||
    /Snapchat/i.test(ua) ||
    /Pinterest/i.test(ua) ||
    /tiktok/i.test(ua)
  );
};

const validLanguages = ["mn", "mn0", "en", "ko", "jp", "de", "se"];
const isValidLanguage = (lang) => validLanguages.includes(lang);

const getAppDeepLinkUrl = (deepLinkBase) => {
  const searchParams = new URLSearchParams(window.location.search);
  const baseLang = searchParams.get("base_lang");
  const studyLang = searchParams.get("study_lang");
  const params = new URLSearchParams();
  if (baseLang) {
    params.set("base_lang", baseLang);
  }
  if (studyLang) {
    params.set("study_lang", studyLang);
  }
  const query = params.toString() ? `?${params.toString()}` : "";
  return `${deepLinkBase}${query}`;
};

const AppDeepLink = ({ data, deepLinkBase }) => {
  const [words, setWords] = useState([]);
  const [showRedirecting, setShowRedirecting] = useState(true);
  const [showFallback, setShowFallback] = useState(false);
  const { setLanguage } = useContext(ThemeContext);
  const deepLinkUrl = getAppDeepLinkUrl(deepLinkBase);
  const redirectingText = data?.redirectingText || "Redirecting ...";
  const openAppFallback = data?.openAppFallback || "Open Tomko";

  useEffect(() => {
    // Check for base_lang query parameter
    const searchParams = new URLSearchParams(window.location.search);
    const baseLang = searchParams.get("base_lang");

    if (baseLang && isValidLanguage(baseLang)) {
      setLanguage(baseLang);
      localStorage.setItem("lingo_portal_app_lang", baseLang);
    }
  }, [setLanguage]);

  useEffect(() => {
    if (data && data.landingText) {
      setWords(data.landingText.split(" "));
    }
  }, [data]);

  useEffect(() => {
    let fallbackTimer;
    const inApp = isInAppBrowser();
    const redirectTimer = setTimeout(() => {
      setShowRedirecting(false);
      if (inApp) {
        // In-app browsers often close the tab on programmatic tomko:// redirect.
        // Show the button so the user can tap (user gesture may open the app or "Open in Tomko" dialog).
        setShowFallback(true);
      } else {
        window.location.href = deepLinkUrl;
        fallbackTimer = setTimeout(() => setShowFallback(true), FALLBACK_DELAY_MS);
      }
    }, REDIRECTING_DURATION_MS);
    return () => {
      clearTimeout(redirectTimer);
      clearTimeout(fallbackTimer);
    };
  }, [deepLinkUrl]);

  const handleOpenApp = () => {
    window.location.href = deepLinkUrl;
  };

  const flagImages = [
    "/images/flags/mongolia.png",
    "/images/flags/us.png",
    "/images/flags/germany.png",
    "/images/flags/japan.png",
    "/images/flags/korea.png",
    "/images/flags/sweden.png",
  ];

  const handleAppStoreClick = (url) => {
    window.open(url, "_blank");
  };

  return (
    <div className="app-deep-link-container">
      <div className="app-deep-link-content">
        <img
          className="app-deep-link-logo"
          src="/images/tomko_logo_trans.png"
          alt="Tomko"
          width={174}
          height={156}
        />
        <div className="greeting-container">
          {showRedirecting && <p className="redirecting-text">{redirectingText}</p>}
          <div className="animated-words-container">
            {words.map((word, index) => (
              <span
                key={`word-${index}`}
                className="animated-word"
                style={{
                  animationDelay: `${index * 0.25}s`,
                }}
              >
                {word}
              </span>
            ))}
          </div>
        </div>

        {showFallback && (
          <>
            <div className="app-fallback-actions">
              <a
                href={deepLinkUrl}
                className="app-open-app-button"
                onClick={(e) => {
                  e.preventDefault();
                  handleOpenApp();
                }}
                aria-label={openAppFallback}
              >
                {openAppFallback}
              </a>
            </div>
            <div className="app-deep-link-app-buttons">
              <a
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="app-deep-link-app-btn app-deep-link-app-btn--android"
                aria-label="Get it on Google Play"
              >
                <img src="/images/android.png" alt="Get it on Google Play" width={192} height={64} />
              </a>
              <a
                href={APP_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="app-deep-link-app-btn app-deep-link-app-btn--ios"
                aria-label="Download on the App Store"
              >
                <img src="/images/ios.png" alt="Download on the App Store" width={192} height={64} />
              </a>
            </div>
            <div className="app-deep-link-screens">
              <img
                className="app-deep-link-screens-img app-deep-link-screens-img--big-desktop"
                src={`${process.env.PUBLIC_URL || ""}/images/tomko_screen_large.png`}
                alt=""
                aria-hidden="true"
              />
              <img
                className="app-deep-link-screens-img app-deep-link-screens-img--desktop"
                src={`${process.env.PUBLIC_URL || ""}/images/tomko_screen_medium.png`}
                alt=""
                aria-hidden="true"
              />
              <img
                className="app-deep-link-screens-img app-deep-link-screens-img--mobile"
                src={`${process.env.PUBLIC_URL || ""}/images/tomko_screen_small.png`}
                alt=""
                aria-hidden="true"
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AppDeepLink;

